import { APP_INITIALIZER, Component, enableProdMode, inject, Injectable, Injector, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { DxLoadPanelModule } from 'devextreme-angular';
import { LayoutService } from './core/services/store/layout.service';
import { ENVIRONMENT_CONFIGURATION } from '@softwarehaus/util-angular-configuration';
import { NappEnvironmentConfiguration } from '~/app/config/environments-configuration';
import { HAMMER_GESTURE_CONFIG, HammerGestureConfig, Meta } from '@angular/platform-browser';
import { ThemeService } from '~/app/core/services/theme.service';
import { setAppApiStrategy } from './app-api-strategy';
import { setAppTranslation } from './app-app-translation';
import { setAppAngularBridge } from './app-bridge';
import { setAppInjector } from './app-injector';
import { setAppRouterHistory } from './app-router-history';
import { AngularBridgeService } from './core/services/angular-bridge.service';
import { BaseModelServiceApiStrategy } from './core/services/backend/base-model-api.service.strategy';
import { RouterHistoryService } from './core/services/router-history.service';
import { AppTranslationService } from './core/services/translation/app-translation.service';

@Injectable({ providedIn: 'root' })
export class CustomHammerConfig extends HammerGestureConfig {
  public override overrides: { [key: string]: object } = {
    pinch: { enable: false },
    rotate: { enable: false },
  };
}

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, DxLoadPanelModule],
  templateUrl: './app.component.html',
  providers: [
    { provide: APP_INITIALIZER, useFactory: initializeApp, deps: [AngularBridgeService], multi: true },
    { provide: HAMMER_GESTURE_CONFIG, useClass: CustomHammerConfig },
    ThemeService,
  ],
})
export class AppComponent implements OnInit {
  private injector = inject(Injector);
  private apiStrategy = inject(BaseModelServiceApiStrategy);
  private routerHistory = inject(RouterHistoryService);
  private appTranslation = inject(AppTranslationService);
  private angularBridge = inject(AngularBridgeService);
  private meta = inject(Meta);
  private config = inject<NappEnvironmentConfiguration>(ENVIRONMENT_CONFIGURATION);

  constructor() {
    if (this.config.environmentName !== 'local') enableProdMode();

    setAppInjector(this.injector);
    setAppApiStrategy(this.apiStrategy);
    setAppRouterHistory(this.routerHistory);
    setAppTranslation(this.appTranslation);
    setAppAngularBridge(this.angularBridge);

    const appleAppID = this.config.appleAppID;
    const baseUrl = this.config.appUrl;
    if (appleAppID) {
      const appleAppLinkMetaTagName = 'apple-itunes-app';
      const appleAppLinkMetaTagKey = `name=${appleAppLinkMetaTagName}`;
      this.routerHistory.currentUrl$.subscribe((url) => {
        const appArgument = `${baseUrl}${
          null === url || typeof url === 'undefined' ? '' : '/' === url.charAt(0) ? url : `/${url}`
        }`;
        this.meta.updateTag(
          { name: appleAppLinkMetaTagName, content: `app-id=${appleAppID}, app-argument=${appArgument}` },
          appleAppLinkMetaTagKey,
        );
      });
    }
  }

  public layoutService = inject(LayoutService);
  private themeService = inject(ThemeService);
  private environment = inject<NappEnvironmentConfiguration>(ENVIRONMENT_CONFIGURATION);

  ngOnInit() {
    this.themeService.loadTheme(this.environment.theme);
  }
}

export function initializeApp(appInitService: AngularBridgeService) {
  return (): Promise<void> => {
    return appInitService.init();
  };
}
